/**
 * Beeldbellen: wat de cliënt nu ziet, plus wat de behandelaar kan doen als het misgaat.
 * Gedeeld door het grote scherm en de mobiele bediening.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  describeClientStatus,
  invitationText,
  type ClientPresence,
  type ClientStatusLevel,
} from "../lib/beeldbellen-status";
import "../beeldbellen.css";

const RESTORED_NOTICE_MS = 6000;

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Zonder https (bv. dev via een netwerkadres) bestaat de klembord-API niet.
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    area.remove();
    return ok;
  }
}

/** Knop die de uitnodiging kopieert en dat ook laat zien. */
export function CopyInvitationButton({
  link,
  label = "Kopieer uitnodiging",
  primary = true,
}: {
  link: string | null;
  label?: string;
  primary?: boolean;
}) {
  const [copied, setCopied] = useState<"idle" | "ok" | "failed">("idle");
  const timer = useRef<number | null>(null);

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);

  const onClick = async () => {
    if (!link) return;
    const ok = await copyText(invitationText(link));
    setCopied(ok ? "ok" : "failed");
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied("idle"), 4000);
  };

  return (
    <span className="bb-copy">
      <button
        type="button"
        className={`jari-btn ${primary ? "jari-btn--primary" : "jari-btn--secondary"}`}
        onClick={onClick}
        disabled={!link}
      >
        {copied === "ok" ? "Gekopieerd ✓" : label}
      </button>
      {copied === "failed" && link && (
        <span className="bb-copy-fallback" role="status">
          Kopiëren lukte niet. Selecteer de link en kopieer hem zelf: <code>{link}</code>
        </span>
      )}
    </span>
  );
}

function levelIcon(level: ClientStatusLevel): string {
  if (level === "ok") return "✓";
  if (level === "lost") return "!";
  if (level === "warn") return "!";
  return "…";
}

/**
 * Wat de cliënt nu ziet, plus wat de behandelaar kan doen als het misgaat.
 * Gedeeld door het grote scherm en de mobiele bediening.
 */
export function ClientStatusBanner({
  presence,
  clientLink,
  onPause,
  compact = false,
  extraActions,
}: {
  presence: ClientPresence;
  clientLink: string | null;
  /** Zet de bal op pauze, zodat hij bij de cliënt stopt zodra de verbinding terug is. */
  onPause?: () => void;
  compact?: boolean;
  extraActions?: ReactNode;
}) {
  const status = describeClientStatus(presence);
  const prevLevel = useRef<ClientStatusLevel>(status.level);
  const [restored, setRestored] = useState(false);

  useEffect(() => {
    const wasLost = prevLevel.current === "lost";
    prevLevel.current = status.level;
    if (!wasLost || status.level === "lost") return;
    setRestored(true);
    const timer = window.setTimeout(() => setRestored(false), RESTORED_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [status.level]);

  const lost = status.level === "lost";

  return (
    <div
      className={`bb-status bb-status--${status.level}${compact ? " bb-status--compact" : ""}`}
      role={lost || status.level === "warn" ? "alert" : "status"}
      aria-live={lost ? "assertive" : "polite"}
    >
      <div className="bb-status-head">
        <span className="bb-status-icon" aria-hidden="true">
          {levelIcon(status.level)}
        </span>
        <div className="bb-status-text">
          <strong>{restored ? "Je cliënt is weer verbonden" : status.title}</strong>
          <span>{status.detail}</span>
        </div>
        {extraActions}
      </div>

      {lost && (
        <div className="bb-status-help">
          <p className="bb-status-help-title">Wat je nu kunt doen</p>
          <ol>
            <li>Laat je cliënt via het beeldbellen weten dat de oefening even stopt.</li>
            <li>
              Vraag je cliënt de pagina te verversen of de link opnieuw te openen, en opnieuw aan
              te geven er klaar voor te zijn.
            </li>
            <li>Ga verder zodra hier «Je cliënt is klaar» staat.</li>
          </ol>
          <div className="bb-status-actions">
            {onPause && (
              <button type="button" className="jari-btn jari-btn--primary" onClick={onPause}>
                Zet de bal op pauze
              </button>
            )}
            <CopyInvitationButton link={clientLink} label="Kopieer de link opnieuw" primary={false} />
          </div>
          {onPause && (
            <p className="bb-status-note">
              Op pauze stopt de bal bij je cliënt zodra de verbinding terug is.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
